/**
 * The airframe register: every aircraft on the books, what it physically is,
 * and what is flyable.
 *
 * The summary is the point of the readiness side: owned is not flyable, and
 * only an explicitly serviceable airframe counts toward available. The identity
 * side keys an airframe on its autopilot board, not on the drone id it happens
 * to be using, so an aircraft keeps its history when it moves ports.
 */

import { api, type ApiResult } from '@/api/client';

export type InventoryStatus = 'serviceable' | 'maintenance' | 'grounded' | 'unknown';
export type InventoryKind = 'copter' | 'plane' | 'vtol' | 'boat' | 'rover' | 'other';

export const INVENTORY_KINDS: InventoryKind[] = ['copter', 'plane', 'vtol', 'boat', 'rover', 'other'];
export const INVENTORY_STATUSES: InventoryStatus[] = [
  'serviceable',
  'maintenance',
  'grounded',
  'unknown',
];

export interface InventoryItem {
  itemId: string;
  tailNumber: string;
  /** Tail number, or a name made from the board serial until somebody gives one. */
  name: string;
  kind: InventoryKind;
  status: InventoryStatus;
  flightHours: number;
  notes: string;
  boardUid: string | null;
  platformId: string | null;
  platform: string | null;
  manufacturer: string | null;
  serialNumber: string;
  identified: boolean;
  simulated: boolean;
  teamId: string | null;
  identifiedBy: string | null;
  identifiedAt: number | null;
  firstSeen: number | null;
  lastSeen: number | null;
  retired: boolean;
  retiredAt: number | null;
  /** The drone id it is on right now, if it is connected. */
  currentSlot: string | null;
}

export interface InventorySummary {
  total: number;
  available: number;
  unavailable: number;
  counts: Record<InventoryStatus, number>;
}

export interface InventoryResponse {
  items: InventoryItem[];
  summary: InventorySummary;
}

export interface Component {
  componentId: string;
  itemId: string;
  category: string;
  make: string;
  model: string;
  serial: string;
  firmware: string;
  notes: string;
  /** "manual", or "autopilot" / "camera" when the aircraft reported it. */
  source: string;
  fittedAt: number;
  removedAt: number | null;
}

export interface SlotHistory {
  slotId: number;
  itemId: string | null;
  droneId: string;
  link: string;
  sysid: number | null;
  simulated: boolean;
  connectedBy: string | null;
  connectedAt: number;
  disconnectedAt: number | null;
}

export interface Revision {
  entity: 'airframe' | 'component' | 'platform';
  entityId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: number;
}

export interface AirframeDetail {
  airframe: InventoryItem;
  components: Component[];
  slots: SlotHistory[];
  revisions: Revision[];
}

export interface ComponentInput {
  category: string;
  make: string;
  model: string;
  serial: string;
  firmware: string;
  notes?: string;
}

export interface IdentifyInput {
  tailNumber: string;
  kind: InventoryKind;
  platform: string;
  manufacturer: string;
  serialNumber: string;
  notes: string;
  components: ComponentInput[];
  status?: InventoryStatus;
  teamId?: string | null;
}

export interface BoardInfo {
  uid: string;
  vendor: string;
  vendorId: number;
  productId: number;
  boardId: number;
  flightSw: string;
  flightGit: string;
  osSw: string;
}

export interface DetectedComponent {
  category: string;
  make: string;
  model: string;
  serial: string;
  firmware: string;
  source: string;
}

/** A live drone id whose aircraft nobody has identified yet. */
export interface PendingIdentification {
  droneId: string;
  slotId: number;
  itemId: string | null;
  simulated: boolean;
  kind: InventoryKind;
  board: BoardInfo | null;
  cameras: Array<{ vendor: string; model: string; firmware: string; resolution: string }>;
  components: DetectedComponent[];
  /** Username of whoever brought the link up, when anybody did. */
  connectedBy: string | null;
  /** True when that is the caller: the only person asked unprompted. */
  mine: boolean;
  /** The caller already said "Skip for now" for this aircraft. */
  skipped: boolean;
}

export interface Platform {
  platformId: string;
  name: string;
  manufacturer: string;
  airframes: number | null;
  batteryWh: number | null;
  reservePct: number | null;
  claimedEnduranceMin: number | null;
  claimedRangeKm: number | null;
  claimedCruiseMs: number | null;
  claimedMaxSpeedMs: number | null;
  claimedCeilingM: number | null;
  claimedMaxWindMs: number | null;
  claimedPayloadKg: number | null;
}

export type PlatformSpecInput = Partial<Omit<Platform, 'platformId' | 'name' | 'manufacturer' | 'airframes'>>;

export interface AirframePatch {
  tailNumber?: string;
  kind?: InventoryKind;
  status?: InventoryStatus;
  flightHours?: number;
  notes?: string;
  serialNumber?: string;
  platform?: string;
  manufacturer?: string;
}

export function getInventory(includeRetired = false): Promise<ApiResult<InventoryResponse>> {
  const query = includeRetired ? '?includeRetired=true' : '';
  return api.get<InventoryResponse>(`/inventory${query}`, { logged: false });
}

/** Replace the whole register in one write. Kept for scripts; the screens edit
 * one airframe at a time, so a stale screen cannot strike off an aircraft that
 * connected after it loaded. */
export function saveInventory(items: InventoryItem[]): Promise<ApiResult<InventoryResponse>> {
  return api.put<InventoryResponse>('/inventory', { items }, { label: 'INVENTORY SAVE' });
}

export function getAirframe(itemId: string): Promise<ApiResult<AirframeDetail>> {
  return api.get<AirframeDetail>(`/inventory/${encodeURIComponent(itemId)}`, { logged: false });
}

export function catalogueAirframe(input: IdentifyInput): Promise<ApiResult<AirframeDetail>> {
  return api.post<AirframeDetail>('/inventory', input, { label: 'CATALOGUE AIRFRAME' });
}

export function updateAirframe(
  itemId: string,
  patch: AirframePatch,
): Promise<ApiResult<AirframeDetail>> {
  return api.patch<AirframeDetail>(`/inventory/${encodeURIComponent(itemId)}`, patch, {
    label: 'AIRFRAME EDIT',
  });
}

/** Deletes an airframe nobody flew; retires one that has. `outcome` says which. */
export function removeAirframe(
  itemId: string,
): Promise<ApiResult<{ itemId: string; outcome: 'retired' | 'deleted' }>> {
  return api.delete(`/inventory/${encodeURIComponent(itemId)}`, { label: 'AIRFRAME REMOVE' });
}

export function fitComponent(
  itemId: string,
  input: ComponentInput,
): Promise<ApiResult<AirframeDetail>> {
  return api.post<AirframeDetail>(`/inventory/${encodeURIComponent(itemId)}/components`, input, {
    label: 'COMPONENT FIT',
  });
}

export function removeComponent(componentId: string): Promise<ApiResult<AirframeDetail>> {
  return api.delete<AirframeDetail>(`/inventory/components/${encodeURIComponent(componentId)}`, {
    label: 'COMPONENT REMOVE',
  });
}

export function getPending(): Promise<ApiResult<{ pending: PendingIdentification[] }>> {
  return api.get<{ pending: PendingIdentification[] }>('/inventory/pending', { logged: false });
}

export function getClaimable(): Promise<ApiResult<{ airframes: InventoryItem[] }>> {
  return api.get<{ airframes: InventoryItem[] }>('/inventory/catalog', { logged: false });
}

export function identifySlot(
  droneId: string,
  input: IdentifyInput,
): Promise<ApiResult<AirframeDetail>> {
  return api.post<AirframeDetail>(
    `/inventory/slots/${encodeURIComponent(droneId)}/identify`,
    input,
    { label: 'IDENTIFY AIRCRAFT' },
  );
}

export function claimSlot(droneId: string, itemId: string): Promise<ApiResult<AirframeDetail>> {
  return api.post<AirframeDetail>(
    `/inventory/slots/${encodeURIComponent(droneId)}/claim`,
    { itemId },
    { label: 'CLAIM AIRCRAFT' },
  );
}

export function skipIdentify(droneId: string): Promise<ApiResult<{ skipped: boolean }>> {
  return api.post<{ skipped: boolean }>(
    `/inventory/slots/${encodeURIComponent(droneId)}/skip`,
    {},
    { logged: false },
  );
}

export function getPlatforms(): Promise<ApiResult<{ platforms: Platform[] }>> {
  return api.get<{ platforms: Platform[] }>('/inventory/platforms', { logged: false });
}

export function savePlatformSpec(
  platformId: string,
  spec: PlatformSpecInput,
): Promise<ApiResult<{ platforms: Platform[] }>> {
  return api.put<{ platforms: Platform[] }>(
    `/inventory/platforms/${encodeURIComponent(platformId)}`,
    spec,
    { label: 'PLATFORM SPEC' },
  );
}
